import React, { useState, useEffect } from 'react';
import './NewsFlashPopup.css';

const DEFAULT_POPUP = {
  title: 'WELCOME TO LGBT PRIDE PATH',
  mainText: 'A Program of the LGBT National Help Center',
  subtitle: 'All programs are free to the public to use',
  website: 'LGBThotline.org',
  extra: [
    'For other support programs please visit: LGBThotline.org',
    'For community resources and connections please visit: LGBTnearMe.org'
  ]
};

/**
 * Build long mock content to test the scroll area.
 * ──────────────────────────────────────────────────────────
 * 🔧 MOCK TEXT: Comment out this entire block OR set USE_MOCK=false
 * once you're done testing the scroll behavior.
 */
const USE_MOCK = false; // ← toggle to true to force mock content (or comment this whole block)
const makeMockData = () => ({
  title: 'IMPORTANT UPDATE – MOCK',
  mainText:
    'This is mock content to test scrolling. Below are many lines to force overflow.',
  subtitle:
    'If you can scroll inside this box on desktop and mobile, it works!',
  website: 'example.org',
  extra: Array.from({ length: 50 }, (_, i) =>
    `Mock paragraph #${i + 1}: ${'Lorem ipsum dolor sit amet, consectetur adipiscing elit. '.repeat(3)}`
  )
});
// ──────────────────────────────────────────────────────────

const NewsFlashPopup = ({ showOnSearchScreen = false }) => {
  const [isVisible, setIsVisible] = useState(false);
  const [popupData, setPopupData] = useState(DEFAULT_POPUP);
  const [isLoading, setIsLoading] = useState(true);

  const handleClose = () => {
    setIsVisible(false);
    document.body.classList.remove('news-flash-open');
  };

  const handleOverlayClick = (e) => {
    if (e.target === e.currentTarget) handleClose();
  };

  useEffect(() => {
    if (!showOnSearchScreen) return;

    let aborted = false;
    const API_BASE_URL =
      process.env.REACT_APP_API_URL || 'https://pridepath-webapp.onrender.com';

    const openWith = (data) => {
      if (aborted) return;
      setPopupData(data);
      setIsVisible(true);
      document.body.classList.add('news-flash-open');
    };

    const fetchPopupContent = async () => {
      try {
        // If mock testing is enabled, open immediately with mock text
        if (USE_MOCK) {
          console.log('📋 NewsFlashPopup: Using MOCK data for testing');
          openWith(makeMockData());
          setIsLoading(false);
          return;
        }

        // Cache-bust and avoid conditional 304s
        console.log('🔄 NewsFlashPopup: Fetching remote content from API...');
        const res = await fetch(
          `${API_BASE_URL}/api/news-flash-remote?t=${Date.now()}`,
          { credentials: 'include', cache: 'no-store' }
        );

        const text = res.ok ? await res.text() : '';
        console.log('📡 NewsFlashPopup: API Response received:', res.ok ? 'Success' : 'Failed');
        console.log('📄 NewsFlashPopup: Response length:', text.length);
        
        // Check if response is empty (no popup should be shown)
        if (!text || text.trim().length === 0) {
          console.log('📭 NewsFlashPopup: Response is empty, not showing popup');
          setIsLoading(false);
          return; // Don't show popup
        }

        // Parse the plain text response (no HTML parsing needed)
        const lines = text
          .split(/\r\n|\n|\r/)
          .map((l) => l.trim())
          .filter(Boolean);

        if (lines.length > 0) {
          // Build the popup data from the lines
          const data = {
            title: lines[0] || '',
            mainText: lines[1] || '',
            subtitle: lines[2] || '',
            website: lines[3] || '',
            extra: lines.slice(4) // render remaining lines as paragraphs
          };

          console.log('✅ NewsFlashPopup: Showing popup with custom content');
          console.log('📝 NewsFlashPopup: Content loaded:', {
            title: data.title,
            linesCount: lines.length,
            hasExtraContent: data.extra.length > 0
          });
          
          openWith(data);
        } else {
          console.log('⚠️ NewsFlashPopup: No valid content lines found, not showing popup');
        }
        
      } catch (err) {
        console.error('❌ NewsFlashPopup fetch error:', err.message);
        console.log('🚫 NewsFlashPopup: Error fetching content, not showing popup');
        // Don't show popup on errors
      } finally {
        if (!aborted) {
          setIsLoading(false);
        }
      }
    };

    // Small UX delay to let the page settle
    const timer = setTimeout(fetchPopupContent, 600);

    // ESC to close
    const onKey = (e) => {
      if (e.key === 'Escape') handleClose();
    };
    window.addEventListener('keydown', onKey);

    return () => {
      aborted = true;
      clearTimeout(timer);
      window.removeEventListener('keydown', onKey);
      document.body.classList.remove('news-flash-open');
    };
  }, [showOnSearchScreen]);

  if (isLoading || !isVisible) return null;

  return (
    <div className="news-flash-overlay" onClick={handleOverlayClick}>
      <div
        className="news-flash-popup"
        role="dialog"
        aria-modal="true"
        aria-label="News update"
      >
        <button
          className="news-flash-close"
          onClick={handleClose}
          aria-label="Close popup"
          type="button"
        >
          ×
        </button>

        <div className="news-flash-content">
          {/* Logo */}
          <div className="news-flash-logo" aria-hidden="true">
            <img 
              src="/logo192.png" 
              alt="PridePath Logo" 
              className="pride-path-logo"
            />
          </div>

          {/* Scrollable message area */}
          <div className="news-flash-scroll" tabIndex="0">
            <div className="news-flash-message">
              {popupData.title ? (
                <h1 className="news-flash-title">{popupData.title}</h1>
              ) : null}

              {popupData.mainText ? (
                <p className="news-flash-main-text">{popupData.mainText}</p>
              ) : null}

              {popupData.subtitle ? (
                <p className="news-flash-subtitle">{popupData.subtitle}</p>
              ) : null}

              {popupData.website ? (
                <a 
                  href={`https://${popupData.website}`} 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="news-flash-website"
                >
                  {popupData.website}
                </a>
              ) : null} 

              {Array.isArray(popupData.extra) &&
                popupData.extra.map((line, i) => {
                  // Parse line to make URLs clickable
                  const parsedLine = line.replace(
                    /(LGBThotline\.org|LGBTnearMe\.org)/gi,
                    (match) => `<a href="https://${match}" target="_blank" rel="noopener noreferrer">${match}</a>`
                  );
                  
                  return (
                    <p 
                      key={i} 
                      className="news-flash-extra"
                      dangerouslySetInnerHTML={{ __html: parsedLine }}
                    />
                  );
                })}
            </div>
          </div>
        </div>

        {/* Hidden admin trigger for debugging */}
        <div
          className="news-flash-admin-trigger"
          onClick={(e) => {
            e.stopPropagation();
            console.log('Admin trigger clicked');
          }}
        />
      </div>
    </div>
  );
};

export default NewsFlashPopup;